import { useEffect, useState, type ReactNode } from "react";
import { api } from "../api/client";
import { isAdmin, ROLE_LABEL, useSession } from "../lib/auth";
import { N_, t, type Lang } from "../lib/i18n";
import { useLive } from "../lib/live";
import { href, type Route } from "../lib/router";
import { ContactModal, useContacts } from "./Contacts";
import { LangSwitch } from "./LangSwitch";
import { PasswordModal } from "./PasswordModal";
import { TotpModal } from "./Totp";
import { cx, Dot, useToast } from "./ui";

const NAV: { route: Route; label: string; match: Route["page"][]; adminOnly?: boolean }[] = [
  { route: { page: "dashboard" }, label: N_("Tableau de bord"), match: ["dashboard"] },
  { route: { page: "inputs" }, label: N_("Entrées"), match: ["inputs", "input"] },
  { route: { page: "outputs" }, label: N_("Sorties"), match: ["outputs"] },
  { route: { page: "matrix" }, label: N_("Matrice"), match: ["matrix"] },
  { route: { page: "journal" }, label: N_("Journal"), match: ["journal"] },
  { route: { page: "users" }, label: N_("Comptes"), match: ["users"], adminOnly: true },
  { route: { page: "requests" }, label: N_("Demandes"), match: ["requests"], adminOnly: true },
  { route: { page: "settings" }, label: N_("Réglages"), match: ["settings"], adminOnly: true },
];

function StatusBar() {
  const { state, connection } = useLive();
  const mtx = state?.mediamtx;
  const alarms = state?.alarms.length ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-muted">
      <span className="inline-flex items-center gap-1.5">
        <Dot tone={connection === "open" ? "ok" : connection === "connecting" ? "warn" : "err"} />
        {connection === "open" ? t("Temps réel") : connection === "connecting" ? t("Connexion…") : t("Hors ligne, reconnexion…")}
      </span>
      {mtx && (
        <span className="inline-flex items-center gap-1.5">
          <Dot tone={mtx.online ? "ok" : "err"} />
          {mtx.online ? "ServerMedia" : t("ServerMedia injoignable")}
        </span>
      )}
      {state && (
        <a href={href({ page: "journal" })} className={cx("inline-flex items-center gap-1.5 hover:text-ink", alarms > 0 && "text-warn")}>
          <Dot tone={alarms > 0 ? "warn" : "ok"} />
          {alarms === 0 ? t("Aucune alarme") : alarms === 1 ? t("1 alarme") : t("{n} alarmes", { n: alarms })}
        </a>
      )}
    </div>
  );
}

function UserMenu() {
  const { me, logout, signedIn } = useSession();
  const toast = useToast();
  // la langue suit le compte sur tous ses navigateurs (le compte de service n'en enregistre pas)
  const saveLang = (lang: Lang) => {
    if (me.id !== 0) api.PUT("/api/auth/language", { body: { lang } }).catch(() => toast(t("Langue non enregistrée sur le compte"), "warn"));
  };
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState(false);
  const [totp, setTotp] = useState(false);
  const [contact, setContact] = useState(false);
  const contacts = useContacts();
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu"
        className="flex items-center gap-2 rounded-md px-2 py-1 text-[13px] hover:bg-raised cursor-pointer">
        <span className="grid size-6 place-items-center rounded-full bg-raised font-cond text-[12px] font-semibold uppercase">
          {me.username.slice(0, 1)}
        </span>
        <span className="hidden sm:inline">{me.username}</span>
        <span className="hidden text-faint md:inline">· {t(ROLE_LABEL[me.role])}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-md border border-line bg-raised shadow-xl">
            <div className="border-b border-line px-3 py-2 text-[12px] text-muted">
              {t("Connecté :")} <span className="text-ink">{me.username}</span><br />{t(ROLE_LABEL[me.role])}
            </div>
            <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2 text-[13px]">
              {t("Langue")}
              <LangSwitch onChange={saveLang} />
            </div>
            {me.id !== 0 && (
              <button type="button" role="menuitem" className="block w-full px-3 py-2 text-left hover:bg-surface cursor-pointer"
                onClick={() => { setOpen(false); setPassword(true); }}>{t("Changer mon mot de passe")}</button>
            )}
            {me.id !== 0 && (
              <button type="button" role="menuitem" className="block w-full px-3 py-2 text-left hover:bg-surface cursor-pointer"
                onClick={() => { setOpen(false); setTotp(true); }}>
                {t("Double authentification")}
                <span className={me.totp_enabled ? "ml-1.5 text-[12px] text-ok" : "ml-1.5 text-[12px] text-faint"}>
                  {me.totp_enabled ? t("active") : t("inactive")}
                </span>
              </button>
            )}
            {contacts && (
              <button type="button" role="menuitem" className="block w-full px-3 py-2 text-left hover:bg-surface cursor-pointer"
                onClick={() => { setOpen(false); setContact(true); }}>{t("Contacter le support")}</button>
            )}
            <button type="button" role="menuitem" className="block w-full px-3 py-2 text-left text-err hover:bg-surface cursor-pointer"
              onClick={logout}>{t("Se déconnecter")}</button>
          </div>
        </>
      )}
      {password && <PasswordModal onClose={() => setPassword(false)} />}
      {totp && <TotpModal me={me} onChanged={signedIn} onClose={() => setTotp(false)} />}
      {contact && contacts && <ContactModal contacts={contacts} onClose={() => setContact(false)} />}
    </div>
  );
}

function useVersion(): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    api.GET("/api/health").then((r) => setVersion(r.data?.gateway_version ?? null)).catch(() => setVersion(null));
  }, []);
  return version;
}

export function Shell({ route, children }: { route: Route; children: ReactNode }) {
  const alarms = useLive().state?.alarms.length ?? 0;
  const { me } = useSession();
  const version = useVersion();
  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <aside className="relative border-b border-line bg-surface lg:sticky lg:top-0 lg:h-screen lg:w-56 lg:shrink-0 lg:border-r lg:border-b-0">
        <div className="flex items-center gap-2.5 px-4 pt-4 pb-3 lg:px-5 lg:pt-5">
          <span className="grid size-7 place-items-center rounded bg-raised">
            <span className="size-3 rounded-full bg-ok shadow-[0_0_8px_var(--color-ok)]" />
          </span>
          <span className="font-cond text-lg font-semibold tracking-wide">Gateway Live</span>
        </div>
        <nav aria-label={t("Navigation")} className="flex gap-1 overflow-x-auto px-2 pb-2 lg:flex-col lg:px-3">
          {NAV.filter((n) => !n.adminOnly || isAdmin(me.role)).map((n) => {
            const active = n.match.includes(route.page);
            return (
              <a key={n.label} href={href(n.route)} aria-current={active ? "page" : undefined}
                className={cx("flex items-center justify-between gap-2 rounded-md px-3 py-2 whitespace-nowrap transition-colors",
                  active ? "bg-raised text-ink" : "text-muted hover:bg-raised/60 hover:text-ink")}>
                {t(n.label)}
                {n.route.page === "journal" && alarms > 0 && (
                  <span className="num rounded bg-warn/20 px-1.5 text-[11px] font-semibold text-warn">{alarms}</span>
                )}
              </a>
            );
          })}
        </nav>
        {version && (
          <div className="hidden px-5 pb-4 font-mono text-[11px] text-faint lg:absolute lg:bottom-0 lg:block">v{version}</div>
        )}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-line bg-bg/90 px-4 py-1.5 backdrop-blur sm:px-6">
          <StatusBar />
          <UserMenu />
        </div>
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-5 sm:px-6">{children}</main>
      </div>
    </div>
  );
}
